import React, { useState } from "react";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { useT } from "@/i18n/LanguageProvider";

const CHANGELOG = [
  {
    version: "v2.6",
    date: "February 2026",
    current: true,
    items: [
      "New section on the sidebar: **Neonatology** — home for Apgar (moved), New Ballard, and Downe.",
      "New tool: **New Ballard Score** — 14-step wizard (sex select → 6 neuromuscular criteria with original schematic SVG illustrations → 6 physical maturity criteria) that estimates gestational age via linear interpolation from the published anchor table. Copy-to-record report + per-criterion breakdown. Bilingual EN/ID.",
      "New tool: **Downe Score** — single-page 5-criteria (RR · retractions · cyanosis · air entry · grunting) neonatal respiratory distress screening. PONED interpretation bands (≤3 mild · 4–5 moderate · ≥6 severe → consider intubation) with colour-coded chips.",
      "Deep Search (⌘K) and Home ➜ Clinical Scores tab now surface both new calculators.",
    ],
  },
  {
    version: "v2.5",
    date: "July 20, 2026",
    current: false,
    items: [
      "Global \"Deep Search\" command palette (Ctrl / ⌘ K) — now includes direct deep-linking to specific pediatric and emergency / ICU medications by name.",
      "New category on the Home page: **Clinical Scores** tab, alongside Medical Algorithm and Drug & Fluid.",
      "New tool: **Killip Classification** for post-MI 30-day mortality risk with Class IV vasopressor deep-link.",
      "New tool: **SOFA & qSOFA** for sepsis screening + full 6-organ dysfunction scoring (Sepsis-3).",
      "New tool: **NEWS2 Score** with dynamic colour-coded segmented controls and sticky-sidebar risk stratification.",
      "New tool: **Apgar Score** in a 2D matrix layout (5 criteria × Minute 1 / 5 / 10) with per-column totals and a clipboard-ready \"6 / 7 / 9\" report line.",
      "Feature update: interactive **Rule of Nines** (Adult, anterior/posterior split) on a humanoid silhouette + **Palm Rule / Lund-Browder advisory** for pediatric burns in the Baxter calculator.",
      "Feature update: **Electrolytes Correction** officially released — added **Geriatric TBW** options (0.5 male, 0.45 female) and strict clinical safety warnings for Na & K correction (ODS prevention, continuous ECG monitoring).",
      "New content collaboration added: **@mednotecard** for series of medical cases and e-books.",
    ],
  },
  {
    version: "v2.1",
    date: "July 5, 2026",
    current: false,
    items: [
      "Major UI revamp in official collaboration with @soresendiribelajar. Introduced a new tab-based navigation system (Medical Algorithm · Drug & Fluid · Guidelines) with Medical Algorithm as the default view.",
      "16 clinical algorithms live across Cardiovascular (ACLS · Acute HF & Cardiogenic Shock · ACS/STEMI · Bradycardia · Tachycardia · Hypertensive Crisis), Respiratory (ARF · Asthma · COPD · ARDS · Choking · Ventilator setup), and Anesthesia & Airway (Difficult Airway · RSI · Malignant Hyperthermia · LAST). Every step is bilingual EN/ID.",
      "Mobile-only 3-column header (brand · toggles · feedback) added — desktop layout untouched.",
      "Pediatric Dosing single-dose label renamed to \"Dose per administration\" / \"Dosis per kali pemberian\".",
      "Sidebar collaborator subtitle now opens the Instagram profile (@soresendiribelajar) in a new tab while keeping typography and color intact.",
    ],
  },
  {
    version: "v2.0",
    date: "June 2026",
    current: false,
    sections: [
      {
        label: "Major Update",
        items: [
          "Integrated Gemini AI Clinical Assistant interface placeholder for real-time bedside decision support.",
          "Added Baxter Resuscitation Calculator for acute burn fluid resuscitation (including 8 h / 16 h splits and macro / micro drip rates).",
          "Integrated Johnson-Toshack Formula in the OB-Gyn section for rapid Estimated Fetal Weight (EFW) calculation.",
        ],
      },
      {
        label: "Minor Update",
        items: [
          "Restructured main layout (moved Emergency & Critical Care to the very top, created \u201cLainnya / Others\u201d section).",
          "Upgraded OB-Gyn section with customizable date-pickers for historical ANC cross-verification.",
          "Cleaned up Pediatric Dosing menu (removed outdated drug list and promoted active calculators out of development status).",
          "Implemented a sleek, space-saving sliding switch for Light / Dark theme toggle to maximize screen estate.",
        ],
      },
    ],
  },
  {
    version: "v1.0",
    date: "Initial public release",
    current: false,
    items: [
      "Emergency & Critical Care section (ACLS infusions + Heparin Protocol with ACS / VTE caps).",
      "OB-Gyn calculator (Naegele EDD, gestational age, trimester).",
      "Top-right Feedback & Donation panel with QR.",
      "Mobile optimisations: numeric keypad, 16 px minimum font, 44 × 44 px touch targets.",
      "IV Fluid Maintenance now also shows Macro (20 gtt) and Micro (60 mgtt) drip rates.",
    ],
  },
  {
    version: "v0.1",
    date: "Initial launch",
    current: false,
    items: [
      "Core fluid maintenance (Holliday-Segar 4-2-1).",
      "Basic pediatric dosing (Paracetamol, Ceftriaxone, Dexamethasone, Ondansetron).",
      "IV drip-rate calculator and electrolyte correction.",
      "NIHSS, Alvarado, BMI calculators with EN / ID language toggle.",
    ],
  },
];

export default function VersionBadge() {
  const t = useT();
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          data-testid="version-badge"
          className="fixed bottom-4 left-4 z-40 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur border border-stone-300 text-stone-700 hover:text-stone-900 hover:border-stone-400 transition-colors duration-100 rounded-full px-3 py-1.5 shadow-sm font-mono text-xs tracking-wider"
          aria-label="Changelog"
        >
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-sky-700" />
          v2.6
        </button>
      </DialogTrigger>

      <DialogContent
        data-testid="changelog-dialog"
        className="max-w-xl border border-stone-700 bg-stone-950 text-stone-100 rounded-sm"
      >
        <div className="pb-3 border-b border-stone-800">
          <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-sky-400">
            {t("changelog.kicker")}
          </div>
          <h2 className="font-display text-xl tracking-tight mt-1">
            {t("changelog.title")}
          </h2>
        </div>

        <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2 -mr-2">
          {CHANGELOG.map((entry) => (
            <section key={entry.version} data-testid={`changelog-${entry.version}`}>
              <div className="flex items-baseline gap-3 mb-2">
                <span
                  className={
                    "font-mono text-sm font-semibold " +
                    (entry.current ? "text-sky-400" : "text-stone-400")
                  }
                >
                  {entry.version}
                </span>
                <span className="text-[11px] tracking-[0.14em] uppercase text-stone-500">
                  {entry.current ? `${t("changelog.current")} · ${entry.date}` : entry.date}
                </span>
              </div>
              <ul className="space-y-1.5">
                {!entry.sections &&
                  entry.items.map((item, i) => (
                    <li
                      key={i}
                      className="text-sm text-stone-300 leading-relaxed flex gap-2"
                    >
                      <span className="text-stone-600 mt-1 shrink-0">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
              </ul>
              {entry.sections && (
                <div className="space-y-4">
                  {entry.sections.map((sec) => (
                    <div key={sec.label} data-testid={`section-${sec.label.toLowerCase().replace(/\s+/g, "-")}`}>
                      <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-sky-400 mb-1.5">
                        [{sec.label}]
                      </div>
                      <ul className="space-y-1.5">
                        {sec.items.map((item, i) => (
                          <li
                            key={i}
                            className="text-sm text-stone-300 leading-relaxed flex gap-2"
                          >
                            <span className="text-stone-600 mt-1 shrink-0">•</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
